import _ from "lodash";
import DataTable from "../../../core/charts/Datatable";
import translate from "../../../core/tools/translate";
import numeral from "numeral";
import * as topojson from 'topojson-client';

const getPeriod = (yearInitial, yearEnd, separator = '\n') =>
    yearEnd
        ? `${yearInitial} ${separator} ${yearEnd}`
        : `${yearInitial}`;

const reducers = {
    vehicleRepairableYear(data) {
        const output = {
            vehicleRepairableYear: {
                chart: null,
                table: null
            }
        };

        output.vehicleRepairableYear.chart = new DataTable(
            data.map(x => ({
                Ano: getPeriod(x.yearInitial, x.yearEnd),
                value: x.value
            })),
            {
                row: 'Frota',
                column: 'Ano',
                value: 'value',
            });

        output.vehicleRepairableYear.table = {
            data: data.map(x => ({
                year: getPeriod(x.yearInitial, x.yearEnd, 'a'),
                value: x.value.toLocaleString().length > 0
                    ? numeral(x.value).format('0,0')
                    : '-',
                variation: x.variation
            })),
            columns: [
                {
                    field: 'year',
                    title: translate('Common.Year')
                },
                {
                    field: 'value',
                    title: translate('Common.RepairableFleetArea')
                },
                {
                    field: 'variation',
                    title: `${translate('Common.Variation')} (%)`,
                    template: dataItem => dataItem.variation
                        ? `<div class="variation-column">
                            <div>
                                <span>${dataItem.variation}%</span>
                                <i class="fa fa-long-arrow-${dataItem.variation > 0 ? 'up' : 'down'}"></i>
                            </div>
                           </div>`
                        : '-',
                }
            ]
        };

        return output;
    },

    vehicleRepairableBrand(data) {
        const output = {
            vehicleRepairableBrand: {
                table: null
            }
        };

        output.vehicleRepairableBrand.table = {
            data: data.map(x => ({
                brandName: x.name,
                total: x.value.toLocaleString().length > 0
                    ? numeral(x.value).format('0,0')
                    : '-',
                percentualTotal: `${x.percentualTotal}%`
            })),
            columns: [
                {
                    field: 'brandName',
                    title: translate('Common.Brand'),
                    footerTemplate: 'Total',
                },
                {
                    field: 'total',
                    title: translate('Common.Total'),
                    footerTemplate: () => numeral(_(data).sumBy('value')).format('0,0'),
                },
                {
                    field: 'percentualTotal',
                    title: `${translate('Common.Total')} (%)`,
                    footerTemplate: '-',
                }
            ]
        };

        return output;
    },

    vehicleRepairableDataEvoluation(data, columnProperty, columnHeader) {
        const years = _.uniqBy(data.map(x => ({ yearInitial: x.yearInitial, yearEnd: x.yearEnd })), x => x.yearInitial);

        const result = {
            values: _(data)
                .groupBy('name')
                .reduce((acc, value, key) => {
                    const row = {
                        [columnProperty]: key,
                    };

                    years.forEach(year => {
                        const yearData = value.find(x => x.yearInitial === year.yearInitial);
                        const yearTotal = data.filter(x => x.yearInitial === year.yearInitial).reduce((acc, val) => acc + val.value, 0);
                        row['total_' + year.yearInitial] = yearData ? yearData.value : 0;
                        row['percentualTotal_' + year.yearInitial] = yearData && yearTotal !== 0 ? yearData.value / yearTotal : 0;
                        row['footerTotal_' + year.yearInitial] = yearTotal;
                    });

                    acc.push(row);
                    return acc;
                }, []),
            aggregate: _(data)
                .groupBy('name')
                .reduce((acc, value, key) => {
                    const row = [];
                    _.forEach(value, val => {
                        row.push({ field: `total_${val.yearInitial}`, aggregate: 'sum' });
                        row.push({ field: `percentualTotal_${val.yearInitial}`, aggregate: 'sum' });
                    });

                    acc = acc.concat(row);
                    return acc;
                }, [])

        };

        const output = {
            dataSource: {
                data: result.values,
                aggregate: result.aggregate
            },
            columns: [
                {
                    field: columnProperty,
                    title: columnHeader,
                },
            ]
        };

        years.forEach(year => {
            output.columns.push({
                title: getPeriod(year.yearInitial, year.yearEnd, 'a'),
                columns: [
                    {
                        field: `total_${year.yearInitial}`,
                        title: translate('Common.Total'),
                        template: dataItem => numeral(dataItem['total_' + year.yearInitial]).format('0,0'),
                        footerTemplate: () => {
                            return numeral(_(result.values).filter(x => x['footerTotal_' + year.yearInitial]).first()['footerTotal_' + year.yearInitial]).format('0,0');
                        },
                    },
                    {
                        field: `percentualTotal_${year.yearInitial}`,
                        title: `${translate('Common.Total')} (%)`,
                        template: dataItem => numeral(dataItem['percentualTotal_' + year.yearInitial]).format('0%'),
                    }]
            });
        });

        return output;
    },

    vehicleRepairableSegment(data) {
        return new DataTable(data.map(x => ({ ['Frota Reparável']: x.value, name: x.name })), {
            row: ['Frota Reparável'],
            column: 'name'
        });
    },

    storesRanking(data) {
        const output = {
            storeRanking: {
                table: null
            }
        };

        output.storeRanking.table = {
            data: data.map(x => ({
                network: x.networkName,
                stores: x.storeCount,
                networkType: x.networkType
            })),
            columns: [
                {
                    field: 'network',
                    title: 'Rede',
                    footerTemplate: 'Total',
                },
                {
                    field: 'stores',
                    title: 'Lojas (Total)',
                    footerTemplate: () => {
                        return numeral(_(data).sumBy('storeCount')).format('0,0');
                    },
                },
                {
                    field: 'networkType',
                    title: `Tipo de Rede`,
                    footerTemplate: '-',
                }
            ]
        };

        return output;
    },

    brandsRanking(data) {
        const output = {
            brandRanking: {
                table: null
            }
        };

        output.brandRanking.table = {
            data: data.map(x => ({
                brand: x.brandName,
                stores: x.storeCount
            })),
            columns: [
                {
                    field: 'brand',
                    title: translate('Common.AfterSalesBrand'),
                    footerTemplate: 'Total',
                },
                {
                    field: 'stores',
                    title: translate('Common.Stores'),
                    footerTemplate: () => {
                        return numeral(_(data).sumBy('storeCount')).format('0,0');
                    },
                }
            ]
        };

        return output;
    },

    protectArea(data) {
        return topojson.feature(data, data.objects.output);
    },

    serviceArea(data) {
        return topojson.feature(data, data.objects.output);
    },

    multicriteriaCalculateds(data) {
        const groupTheme = _(data)
            .groupBy('theme')
            .map((f, key) => ({
                theme: key,
                values: f
                    .map(x => {
                        x.points = _(f).sumBy('evaluationValue');
                        return x;
                    })
            }))
            .value();

        const output = {
            tables: {}
        };

        const themes = _(groupTheme)
            .map((x, index) => ({ index, theme: x.theme }))
            .value();

        _(themes).forEach(item => {
            const table = {
                dataSource: _(groupTheme[item.index].values)
                    .orderBy('order')
                    .map(x => ({
                        multicriteriaId: x.multicriteriaId,
                        kpi: x.kpi,
                        kpiId: x.kpiId,
                        kpiDescription: x.kpiDescription,
                        total: x.total,
                        range: x.range,
                        evaluation: x.evaluationValue,
                        points: x.points,
                        score: numeral(_.divide(_.sumBy(data, 'evaluationValue'), data.length)).format('0.0')
                    }))
                    .value(),
                columns: [
                    {
                        field: 'kpi',
                        title: 'KPI',
                        width: '90px',
                    },
                    {
                        field: 'total',
                        title: 'Total',
                        width: '38px',
                        format: '0,0',
                    },
                    {
                        field: 'range',
                        title: 'Range',
                        width: '49px',
                    },
                    {
                        field: 'evaluation',
                        title: 'Nota',
                        width: '26px',
                    },
                    {
                        field: 'points',
                        title: 'Pontos',
                        width: '38px',
                    }
                ]
            };

            output.tables[item.theme] = table;
        });

        return output;
    },

    serviceAreaIndicatorsCalculated(data) {
        const rows = (data || []).map(item => {
            const primary = Number((item && item.valuePrimaryInfluenceArea != null) ? item.valuePrimaryInfluenceArea : 0);
            const second = Number((item && item.valueSecondInfluenceArea != null) ? item.valueSecondInfluenceArea : 0);
            const tertiary = Number((item && item.valueTertiaryInfluenceArea != null) ? item.valueTertiaryInfluenceArea : 0);
            const external = Number((item && item.valueExternalInfluenceArea != null) ? item.valueExternalInfluenceArea : 0);
            const total = Number((item && item.valueTotalInfluenceArea != null) ? item.valueTotalInfluenceArea : 0);

            return {
                groupDescription: item.groupDescription,
                formula: item.formula,
                valueDescription: item.valueDescription,
                formatInterface: item.formatInterface,
                primary,
                second,
                tertiary,
                external,
                total
            };
        });

        const formatValue = (value, format) => {
            return numeral(value).format(format);
        };

        return {
            dataSource: {
                data: rows,
                sort: [{ field: 'groupDescription', dir: 'asc' }]
            },
            dataBound: function (e) {
                const grid = e.sender;
                const rows = grid.tbody.find('> tr');

                let firstCell = null;
                let lastValue;
                let rowspan = 1;

                rows.each(function () {
                    const row = $(this);
                    const item = grid.dataItem(row);
                    const currentValue = item.groupDescription;
                    const currentCell = row.children('td').eq(0);

                    if (firstCell && currentValue === lastValue) {
                        rowspan++;
                        firstCell.attr('rowspan', rowspan);
                        currentCell.remove();
                    } else {
                        lastValue = currentValue;
                        rowspan = 1;
                        firstCell = currentCell;
                        firstCell.css({
                            'border-right': '1px solid #DDDDDD',
                            'vertical-align': 'middle'
                        });
                        firstCell.next('td').css('border-left', '0');
                    }
                });
            },
            columns: [
                {
                    title: 'Grupos',
                    columns: [
                        { field: 'groupDescription', title: ' ' }
                    ]
                },
                {
                    title: 'Valores',
                    columns: [
                        { field: 'formula', title: ' ' },
                        { field: 'valueDescription', title: ' ' }
                    ]
                },
                {
                    title: 'Trade Area',
                    columns: [
                        { field: 'primary', title: 'Primary Trade Area', template: d => formatValue(d.primary, d.formatInterface) },
                        { field: 'second', title: 'Secondary Trade Area', template: d => formatValue(d.second, d.formatInterface) },
                        { field: 'tertiary', title: 'Tertiary Trade Area', template: d => formatValue(d.tertiary, d.formatInterface) },
                        { field: 'external', title: 'Outside Trade Area', template: d => formatValue(d.external, d.formatInterface) },
                        { field: 'total', title: 'Total Geral', template: d => formatValue(d.total, d.formatInterface) }
                    ]
                }
            ]
        };
    }
};

export default reducers;





// WEBPACK FOOTER //
// ./src/scripts/api/endpoints/afterSales/reducers.js